import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { addV3PersonLink, endV3PersonLink, setV3MainAccount, searchV3 } from '@/services/v3.service.js';
import { ROLE_WORDS, LINK_ADD_ROLES, LINK_DOOR_WORDS as W, word } from './words.js';

// THE LINK DOORS ON THE V3 PEOPLE SECTION (account-people-door T27 over T26).
// David's ruling (2026-10-25): TRACON's account_people is the record of who is on
// an account and in which role; a person on many accounts is normal; Zoho's
// Account_Name is a copy of the person's main account and only these doors move
// it. One shadcn Dialog per act, opened by PeopleSection only while the account
// payload says people_links.doors_open (account_people "both"):
//   add       search people (v3 search, people rows only), pick one and a role
//   add_role  the same with the person already chosen (no search)
//   end       a reason is required; the link is ended, never deleted
//   make_main the Zoho copy follows (said before the click)
// Each posts through v3.service only. A refusal renders the server's broker
// sentence in the Dialog (api.js leaves these codes to the caller, so there is
// no second toast). A landed act fires one toast, then ONE toast.info PER SERVER
// WARNING (a warning routed to the console is a warning not issued), closes and
// re-reads the account. The success words come from the answer's read-back,
// never from what was typed. Derives nothing: the server decides every state.

const FIELD = 'flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent';
const LANDED = new Set(['created', 'reopened', 'restated']);

function refusalOf(e) {
  const body = e && e.body ? e.body : {};
  return { code: body.error || (e && e.code) || 'error', message: body.message || 'Something went wrong. Reload the account to check what was saved.' };
}

function announce(kind, message, warnings) {
  toast[kind](message);
  for (const w of Array.isArray(warnings) ? warnings : []) toast.info(w);
}

function answerAdd(r, name) {
  const linked = r?.read_back?.ok && r.link ? word(ROLE_WORDS, r.link.role) : null;
  if (LANDED.has(r?.action)) return announce('success', linked ? `${name} is on this account as ${linked}.` : 'Saved.', r.warnings);
  if (r?.action === 'already_open') return announce('info', `${name} is already on this account${linked ? ` as ${linked}` : ''}; nothing changed.`, r.warnings);
  return announce('info', `Nothing changed (${r?.action || 'no answer'}).`, r?.warnings);
}

function answerEnd(r) {
  return announce(r?.read_back?.ok && r.link?.ended_at ? 'success' : 'info', r?.read_back?.ok && r.link?.ended_at ? 'Link ended.' : 'Saved.', r?.warnings);
}

function answerMain(r) {
  if (r?.action === 'already') return announce('info', 'Already the main account; nothing changed.', r.warnings);
  const here = r?.read_back?.ok && r.read_back.main?.this_account === true;
  return announce(here ? 'success' : 'info', here ? 'Main account set.' : 'Saved.', r?.warnings);
}

function PersonSearch({ picked, onPick }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setHits(null); return undefined; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      searchV3(query)
        .then((r) => { if (alive) setHits((r?.data?.results || []).filter((x) => x.type === 'person')); })
        .catch(() => { if (alive) setHits('failed'); })
        .finally(() => { if (alive) setBusy(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="space-y-1.5">
      <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={W.search_placeholder} className={FIELD} data-link-search />
      {busy ? <p className="text-xs text-text-dim">{W.searching}</p> : null}
      {!busy && hits === 'failed' ? <p className="text-xs text-danger" data-link-search-failed>{W.search_failed}</p> : null}
      {!busy && Array.isArray(hits) && hits.length === 0 ? <p className="text-xs text-text-dim" data-link-search-empty>{W.search_empty}</p> : null}
      {!busy && Array.isArray(hits) && hits.length ? (
        <div className="max-h-48 overflow-y-auto rounded-card border border-border">
          {hits.map((h) => (
            <button key={h.id} type="button" onClick={() => onPick({ id: h.id, name: h.label })} data-link-person={h.id} aria-pressed={picked?.id === h.id}
              className={`flex min-h-[40px] w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-border/30 ${picked?.id === h.id ? 'bg-border/40 font-medium' : ''}`}>
              <span className="truncate text-text">{h.label}</span>
              <span className="shrink-0 text-[11px] text-text-dim">{picked?.id === h.id ? 'chosen' : h.sublabel || ''}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * door: null | { kind: 'add' } | { kind: 'add_role', person } | { kind: 'end', person, link } | { kind: 'make_main', person }
 * person: a V3 people row ({ id, display_name }); link: one of its roles ({ link_id, role }).
 */
export default function PersonLinkDialog({ door, account, onClose, onSaved }) {
  const [person, setPerson] = useState(null);
  const [role, setRole] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState(null);
  useEffect(() => {
    setPerson(door?.person ? { id: door.person.id, name: door.person.display_name || '' } : null);
    setRole(''); setReason(''); setSaving(false); setRefusal(null);
  }, [door]);
  if (!door) return null;
  const kind = door.kind;
  const ready = kind === 'end' ? !!reason.trim() : kind === 'make_main' ? !!person : !!person && LINK_ADD_ROLES.includes(role);
  const title = { add: W.add_title, add_role: W.add_role_title, end: W.end_title, make_main: W.make_main_title }[kind];
  const confirmWord = { add: W.confirm_add, add_role: W.confirm_add, end: W.confirm_end, make_main: W.confirm_main }[kind];

  async function submit() {
    if (!ready || saving) return;
    setSaving(true); setRefusal(null);
    try {
      if (kind === 'end') answerEnd(await endV3PersonLink(door.link.link_id, { account_id: account.id, reason: reason.trim() }));
      else if (kind === 'make_main') answerMain(await setV3MainAccount({ account_id: account.id, person_id: person.id }));
      else answerAdd(await addV3PersonLink({ account_id: account.id, person_id: person.id, role }), person.name || 'The person');
      onClose();
      onSaved?.();
    } catch (e) {
      setRefusal(refusalOf(e));
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {kind === 'end' ? <>{door.person?.display_name || 'This person'}, {word(ROLE_WORDS, door.link?.role)} on {account?.name || 'this account'}. {W.end_note}</> : null}
            {kind === 'make_main' ? <>{door.person?.display_name || 'This person'}: {account?.name || 'this account'}. {W.make_main_note}</> : null}
            {kind === 'add_role' ? <>{door.person?.display_name || 'This person'} on {account?.name || 'this account'}.</> : null}
            {kind === 'add' ? <>{account?.name || 'This account'}. A person on other accounts keeps them.</> : null}
          </DialogDescription>
        </DialogHeader>
        {kind === 'add' ? <PersonSearch picked={person} onPick={setPerson} /> : null}
        {kind === 'add' || kind === 'add_role' ? (
          <label className="block text-xs text-text-muted">
            {W.role}
            <select value={role} onChange={(e) => setRole(e.target.value)} className={`mt-1 ${FIELD}`} data-link-role>
              <option value="">{W.pick_role}</option>
              {LINK_ADD_ROLES.map((r) => <option key={r} value={r}>{word(ROLE_WORDS, r)}</option>)}
            </select>
          </label>
        ) : null}
        {kind === 'end' ? (
          <label className="block text-xs text-text-muted">
            {W.reason}
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className={`mt-1 ${FIELD}`} data-link-reason />
          </label>
        ) : null}
        {refusal ? <p className="text-sm text-danger" data-link-error={refusal.code}>{refusal.message}</p> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>{W.cancel}</Button>
          <Button type="button" onClick={submit} disabled={!ready || saving} data-link-confirm>{saving ? W.saving : confirmWord}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
